import { Injectable, signal, computed } from '@angular/core';
import { authUserModel, authModel } from '../authentication/models/auth';
import { PermissionKey, PermissionsMap } from '../authentication/models/permission.types';

@Injectable({ providedIn: 'root' })
export class PermissionService {
    private readonly _allowed = signal<Set<PermissionKey>>(new Set<PermissionKey>());

    // For debugging in template: {{ permissionService.allowedList() | json }}
    public readonly allowedList = computed<PermissionKey[]>(() => {
        return Array.from(this._allowed());
    });

    public constructor() {
        this.loadFromStorage('authUserInformation', 'auth');
    }

    public updateFromUser(user: authUserModel | null): void {
        if (user !== null && user.Permissions !== null && user.Permissions !== undefined) {
            this.applyPermissionRecord(user.Permissions as PermissionsMap);
        } else {
            this._allowed.set(new Set<PermissionKey>());
        }
    }

    public updateFromAuthSnapshot(auth: authModel | null): void {
        if (auth !== null && auth.permissions !== null && auth.permissions !== undefined) {
            this.applyPermissionRecord(auth.permissions as PermissionsMap);
        } else {
            this._allowed.set(new Set<PermissionKey>());
        }
    }

    public has(key: PermissionKey): boolean {
        return this._allowed().has(key);
    }

    public any(keys: PermissionKey[]): boolean {
        const current = this._allowed();
        for (let i = 0; i < keys.length; i++) {
            if (current.has(keys[i]) === true) {
                return true;
            }
        }
        return false;
    }

    public all(keys: PermissionKey[]): boolean {
        const current = this._allowed();
        for (let i = 0; i < keys.length; i++) {
            if (current.has(keys[i]) === false) {
                return false;
            }
        }
        return true;
    }

    public clear(): void {
        this._allowed.set(new Set<PermissionKey>());
    }

    // --- private helpers ---

    private applyPermissionRecord(record: PermissionsMap): void {
        const next = new Set<PermissionKey>();

        const entries = Object.entries(record);
        for (let i = 0; i < entries.length; i++) {
            const key = entries[i][0] as PermissionKey;
            const value = entries[i][1];
            if (value === true) {
                next.add(key);
            }
        }

        // overwrite, so false/missing keys actually hide stuff
        this._allowed.set(next);
    }

    private loadFromStorage(userKey: string, authKey: string): void {
        // Prefer full user
        try {
            const rawUser = localStorage.getItem(userKey);
            if (rawUser !== null) {
                const user = JSON.parse(rawUser) as authUserModel;
                this.updateFromUser(user);
                return;
            }
        } catch {
            // continue
        }

        // Fallback to auth snapshot
        try {
            const rawAuth = localStorage.getItem(authKey);
            if (rawAuth !== null) {
                const auth = JSON.parse(rawAuth) as authModel;
                this.updateFromAuthSnapshot(auth);
                return;
            }
        } catch {
            // continue
        }

        // Nothing in storage
        this._allowed.set(new Set<PermissionKey>());
    }
}
